// src/stores/workflow-variables.ts
import { defineStore } from 'pinia';
import { computed, ref, type ComputedRef, type Ref } from 'vue';

import { contactFieldsOptions } from '../partials/contact-fields-options';
import { Flow, FlowNode, VariableFieldConfig } from '../types/workflow';

import { useWorkflowStore } from './workflow';

export interface WorkflowVariable {
  id: string;
  name: string;
  isProtected?: boolean;
}

const systemVariables: WorkflowVariable[] = [
  { id: 'conversation.id', name: 'conversation.id', isProtected: true },
  { id: 'agent.id', name: 'agent.id', isProtected: true },
  { id: 'agent.name', name: 'agent.name', isProtected: true },
  { id: 'agent.email', name: 'agent.email', isProtected: true },
  { id: 'assigneeUser.name', name: 'assigneeUser.name', isProtected: true },
  { id: 'assigneeUser.email', name: 'assigneeUser.email', isProtected: true },
  { id: 'assigneeTeam.name', name: 'assigneeTeam.name', isProtected: true },
  { id: 'contact.id', name: 'contact.id', isProtected: true },
  { id: 'contact.name', name: 'contact.name', isProtected: true },
  { id: 'contact.phone', name: 'contact.phone', isProtected: true },
  { id: 'contact.email', name: 'contact.email', isProtected: true },
  { id: 'contact.company', name: 'contact.company', isProtected: true },
  { id: 'contact.country', name: 'contact.country', isProtected: true },
  { id: 'system.current_datetime', name: 'system.current_datetime', isProtected: true },
  { id: 'system.current_time', name: 'system.current_time', isProtected: true },
  { id: 'system.current_date', name: 'system.current_date', isProtected: true },
];

export const useWorkflowVariables = defineStore(
  'bvWorkflowVariables',
  (): {
    variables: Ref<WorkflowVariable[]>;
    getVariables: ComputedRef<WorkflowVariable[]>;
    getVariable: (name: string, nodeId?: string) => WorkflowVariable | undefined;
    setVariable: (name: string) => void;
    deleteVariable: (name: string) => void;
    clearVariables: () => void;
    hasVariable: (name: string) => boolean;
    getVariableNames: ComputedRef<string[]>;
    extractVariablesFromFlow: (flow: any) => void;
    getAvailableVariablesForNode: (nodeId: string) => WorkflowVariable[];
    getNodesInExecutionOrder: () => FlowNode[];
    cleanupUnusedVariables: () => void;
    replaceVariableInFlow: (variableName: string) => void;
    fetchCustomAttributesVariables: () => Promise<void>;
  } => {
    // State
    const variables = ref<WorkflowVariable[]>([...systemVariables]);
    const customAttributes = ref<WorkflowVariable[]>([]);
    const store = useWorkflowStore();

    // Getters
    // Combines System, Custom Attributes, and Dynamically created variables
    const getVariables = computed(() => {
      return [...variables.value, ...customAttributes.value];
    });

    // Get specific variable by name
    const getVariable = (name: string) => {
      return getVariables.value.find((variable) => variable.name === name);
    };

    // Get all variable names
    const getVariableNames = computed(() => {
      return getVariables.value.map((variable) => variable.name);
    });

    // Check if variable exists
    const hasVariable = (name: string) => {
      return getVariables.value.some((variable) => variable.name === name);
    };

    // Actions
    const fetchCustomAttributesVariables = async () => {
      const fetchFunc = contactFieldsOptions();
      const { data } = await fetchFunc();
      customAttributes.value = data.map((attr) => ({
        id: `attribute.${attr.value}`,
        name: `attribute.${attr.value}`,
        isProtected: true,
      }));
    };

    const setVariable = (name: string) => {
      const existingVariable = getVariable(name);

      if (!existingVariable) {
        // Create new variable
        const newVariable: WorkflowVariable = {
          id: name,
          name,
        };
        variables.value.push(newVariable);
      }
    };

    // Recursive function to replace variables in nested objects and arrays
    const replaceVariableInValue = (value: any, variableName: string): any => {
      if (typeof value === 'string') {
        // Replace the specific variable with empty string
        const variablePattern = new RegExp(`{{\\s*${variableName}\\s*}}`, 'g');
        let result = value.replace(variablePattern, '');

        // Clean up any remaining empty {{}} patterns
        result = result.replace(/\{\{\s*\}\}/g, '');

        // Trim whitespace and clean up multiple spaces
        result = result.replace(/\s+/g, ' ').trim();

        return result;
      } else if (Array.isArray(value)) {
        // Recursively process array elements
        return value.map((item) => replaceVariableInValue(item, variableName));
      } else if (value && typeof value === 'object') {
        // Recursively process object properties
        const result: any = {};
        for (const [key, val] of Object.entries(value)) {
          result[key] = replaceVariableInValue(val, variableName);
        }
        return result;
      }

      // Return primitive values as-is
      return value;
    };

    const deleteVariable = (name: string) => {
      const variable = getVariable(name);

      // Never delete protected variables
      if (variable?.isProtected) {
        return;
      }

      // Replace all occurrences of the variable with empty strings throughout the flow
      replaceVariableInFlow(name);

      // Remove the variable from the store
      const index = variables.value.findIndex((variable) => variable.name === name);
      if (index !== -1) {
        variables.value.splice(index, 1);
      }
    };

    const clearVariables = () => {
      // Only clear dynamic variables; keep protected ones
      variables.value = [...systemVariables];
      // customAttributes stays because it's managed by fetchCustomAttributes
    };

    /**
     * Helper function to extract variable names from a field value based on configuration
     */
    const extractVariableNamesFromField = (fieldValue: any, config: VariableFieldConfig): string[] => {
      const variableProperty = config.variableProperty || 'variable';
      const variableNames: string[] = [];

      if (config.isArray && Array.isArray(fieldValue)) {
        // Handle array of variables (e.g., responseMap)
        fieldValue.forEach((item) => {
          if (item && typeof item === 'object' && item[variableProperty]) {
            const variableName = item[variableProperty];
            if (typeof variableName === 'string' && variableName.trim()) {
              variableNames.push(variableName.trim());
            }
          }
        });
      } else if (typeof fieldValue === 'object' && fieldValue[variableProperty]) {
        // Handle single variable object (e.g., saveResponse.variable)
        const variableName = fieldValue[variableProperty];
        if (typeof variableName === 'string' && variableName.trim()) {
          variableNames.push(variableName.trim());
        }
      } else if (typeof fieldValue === 'string' && fieldValue.trim()) {
        // Handle direct string value
        variableNames.push(fieldValue.trim());
      }

      return variableNames;
    };

    /**
     * Helper function to extract all variables from a step based on its configuration
     */
    const extractVariablesFromStep = (step: FlowNode): string[] => {
      const stepConfig = store.getStepConfig(step.type);
      if (!stepConfig?.variableFields) {
        return [];
      }

      const allVariableNames: string[] = [];

      stepConfig.variableFields.forEach((config) => {
        // Handle nested field paths like 'saveResponse.variable'
        const fieldValue = getNestedFieldValue(step.data, config.fieldKey);

        // Special handling for ask-question: check if hasVariable toggle is enabled
        if (step.type === 'AskQuestion' && config.fieldKey === 'saveResponse.variable') {
          const hasVariable = getNestedFieldValue(step.data, 'saveResponse.hasVariable');
          if (!hasVariable) {
            return; // Skip extraction if variable toggle is disabled
          }
        }

        if (fieldValue) {
          const variableNames = extractVariableNamesFromField(fieldValue, config);
          allVariableNames.push(...variableNames);
        }
      });

      return allVariableNames;
    };

    /**
     * Get nested field value from object using dot notation
     * @param obj - The object to get the value from
     * @param path - The dot-separated path to the field
     * @returns The value at the specified path
     */
    const getNestedFieldValue = (obj: any, path: string): any => {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : undefined;
      }, obj);
    };

    /**
     * Extract variables from the entire flow using step configurations
     */
    const extractVariablesFromFlow = (flow: Flow) => {
      // Clear existing variables (Reset only dynamic variables)
      variables.value = [...systemVariables];

      // Extract variables from each step
      Object.values(flow).forEach((step) => {
        const variableNames = extractVariablesFromStep(step);
        variableNames.forEach((variableName) => {
          setVariable(variableName);
        });
      });
    };

    /**
     * Get all nodes in execution order (depth-first traversal from root)
     */
    const getNodesInExecutionOrder = (): FlowNode[] => {
      const visited = new Set<string | number>();
      const result: FlowNode[] = [];

      const traverse = (nodeId: string | number) => {
        if (visited.has(nodeId)) {
          return;
        }

        const node = store.flow[nodeId];
        if (!node) {
          return;
        }

        visited.add(nodeId);
        result.push(node);

        // Traverse children in order
        node.children.forEach((childId: string | number) => {
          traverse(childId);
        });
      };

      // Start from root node
      traverse('__root__');
      return result;
    };

    /**
     * Get available variables for a specific node
     * Variables are only available to nodes that come after the step that created them
     */
    const getAvailableVariablesForNode = (nodeId: string): WorkflowVariable[] => {
      const executionOrder = getNodesInExecutionOrder();
      const currentNodeIndex = executionOrder.findIndex((node) => node.id === nodeId);

      // If node not found, we still want to show global variables
      const protectedVariables = getVariables.value.filter((v) => v.isProtected);

      if (currentNodeIndex === -1) {
        return [...protectedVariables];
      }

      const availableVariables: WorkflowVariable[] = [...protectedVariables];

      // Find all nodes that come before the current node and extract their variables
      for (let i = 0; i < currentNodeIndex; i++) {
        const node = executionOrder[i];
        const variableNames = extractVariablesFromStep(node);

        variableNames.forEach((variableName) => {
          // Add the variable if it doesn't already exist
          if (!availableVariables.find((v) => v.name === variableName)) {
            availableVariables.push({
              id: variableName,
              name: variableName,
            });
          }
        });
      }

      return availableVariables;
    };

    /**
     * Check if a variable is still being used anywhere in the flow
     * @param variableName - The name of the variable to check
     * @returns true if the variable is still being used, false otherwise
     */
    const isVariableStillUsed = (variableName: string): boolean => {
      // Check all nodes in the flow
      Object.values(store.flow).forEach((node) => {
        Object.keys(node.data).forEach((fieldKey) => {
          const fieldValue = node.data[fieldKey];

          // Check if the variable is used in this field value
          if (isVariableUsedInValue(fieldValue, variableName)) {
            return true;
          }
        });
      });

      return false;
    };

    /**
     * Recursively check if a variable is used in a field value
     * @param value - The field value to check
     * @param variableName - The variable name to look for
     * @returns true if the variable is found, false otherwise
     */
    const isVariableUsedInValue = (value: any, variableName: string): boolean => {
      if (typeof value === 'string') {
        // Check for {{variableName}} pattern
        const variablePattern = new RegExp(`{{\\s*${variableName}\\s*}}`, 'g');
        return variablePattern.test(value);
      } else if (Array.isArray(value)) {
        // Recursively check array elements
        return value.some((item) => isVariableUsedInValue(item, variableName));
      } else if (value && typeof value === 'object') {
        // Recursively check object properties
        return Object.values(value).some((val) => isVariableUsedInValue(val, variableName));
      }

      return false;
    };

    /**
     * Clean up unused variables by replacing {{variableName}} with empty strings
     * This should be called when field values change or fields are removed
     */
    const cleanupUnusedVariables = () => {
      const variablesToCleanup: string[] = [];

      variables.value.forEach((variable) => {
        // Only cleanup if NOT protected and NOT used
        if (!isVariableStillUsed(variable.name) && !variable.isProtected) {
          variablesToCleanup.push(variable.name);
        }
      });

      // Replace unused variables with empty strings throughout the flow
      variablesToCleanup.forEach((variableName) => {
        replaceVariableInFlow(variableName);

        // Remove the variable from the store after replacement
        const index = variables.value.findIndex((v) => v.name === variableName);
        if (index !== -1) {
          variables.value.splice(index, 1);
        }
      });
    };

    /**
     * Replace all occurrences of {{variableName}} with empty strings throughout the entire flow
     * @param variableName - The variable name to replace
     */
    const replaceVariableInFlow = (variableName: string) => {
      // Process all nodes in the flow
      Object.values(store.flow).forEach((node) => {
        const updatedData: Record<string, any> = {};
        let hasChanges = false;

        Object.keys(node.data).forEach((fieldKey) => {
          const fieldValue = node.data[fieldKey];
          const cleanedField = replaceVariableInValue(fieldValue, variableName);

          // Only update if there were changes
          if (JSON.stringify(fieldValue) !== JSON.stringify(cleanedField)) {
            updatedData[fieldKey] = cleanedField;
            hasChanges = true;
          }
        });

        // Update the node data if there were changes
        if (hasChanges) {
          store.updateNodeData(node.id, updatedData);
        }
      });
    };

    return {
      // State
      variables,
      getVariables,
      getVariable,
      getVariableNames,
      hasVariable,

      // Actions
      setVariable,
      deleteVariable,
      clearVariables,
      extractVariablesFromFlow,
      getAvailableVariablesForNode,
      getNodesInExecutionOrder,
      cleanupUnusedVariables,
      replaceVariableInFlow,
      fetchCustomAttributesVariables,
    };
  },
);
